/*
 * ============================
 * File: main.js
 * Project: Octavia-Admin
 * File Created: Thursday, 9th April 2020 2:11:05 am
 * Author:UILIB
 * AuthorUrl:https://ui-lib.com/
 * -----
 * Last Modified: Tuesday, 14th April 2020 7:17:10 pm
 * Modified By: naime hossain (naime.hossain93@gmail.com)
 * -----
 * Copyright 2020 - 2021 UILIB, UILIB
 * ============================
 */
//!
import notSuspended from '@/directives/not-suspended';

import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';

import vuetify from './plugins/vuetify';
import './plugins';

import '@/scss/global.css';
import '@/scss/globalStyle.scss';

// mock

import firebase from 'firebase/app';

import swiper from 'swiper/swiper-element-bundle.min.js';
import Carousel3d from 'vue-carousel-3d';
import ActionButton from '@/components/base/ActionButton.vue';
import { createSimpleTransition } from 'vuetify/lib/components/transitions/createTransition';

// optional style for arrows & dots
import VueGlide from 'vue-glide-js';
import 'vue-glide-js/dist/vue-glide.css';
import VueFileAgent from 'vue-file-agent';
// eslint-disable-next-line
import VueFileAgentStyles from 'vue-file-agent/dist/vue-file-agent.css';
import VueEllipseProgress from 'vue-ellipse-progress';
import VueMask from 'v-mask';

import reloadOnFail from './directives/reload-on-fail';
import isAvailable from '@/directives/is-available';
import confirm from './directives/confirm';
import VueMeta from 'vue-meta';
import VueAxios from 'vue-axios';
import axios from 'axios';
import VueNavigationBar from 'vue-navigation-bar';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import SocialSharing from 'vue-social-sharing';
import { datadogRum } from '@datadog/browser-rum';
import websocket from '@/services/websocket';

// theme Main scss
import '@/assets/scss/octavia-design-system/octavia-design-system.scss';
import 'vue2-datepicker/index.css';
import '@/scss/dialogStyling.scss';

// plugins menu

// javascript import for when you're importing the css directly in your javascript
import 'vue-navigation-bar/dist/vue-navigation-bar.css';
import isGameActive from '@/directives/is-game-active';
import { CURRENCIES } from './store';
import EventBus from '@/plugins/eventBus';
import { SIGN_UP_STEPS } from '@/helpers/enums';

const myTransition = createSimpleTransition('my-transition');

Vue.directive('is-available', isAvailable);
Vue.directive('is-game-active', isGameActive);
Vue.directive('if-not-suspended', notSuspended);
Vue.directive('confirm', confirm);
Vue.directive('reload-on-fail', reloadOnFail);

Vue.component('my-transition', myTransition);
Vue.component('ActionButton', ActionButton);

Vue.use(VueEllipseProgress);
Vue.use(VueFileAgent);
Vue.use(Carousel3d);
Vue.use(VueMask);
Vue.use(VueGlide);
Vue.use(SocialSharing, {
  networks: {
    gmail: 'https://mail.google.com/mail/?view=cm&fs=1&su=@t&body=@d%0A@url',
  },
});

Vue.use(VueMeta);
Vue.use(swiper);

if (process.env.NODE_ENV === 'prod' || process.env.NODE_ENV === 'production') {
  datadogRum.init({
    clientToken: 'pub1c58fbcbf73561bf71d1faafddc7163d',
    applicationId: 'c9708158-a146-4071-8b2f-90af3cc0e53d',
    site: 'us5.datadoghq.com',
    service: 'webapp',
    env: 'prod',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });

  datadogRum.startSessionReplayRecording();
}

Vue.use(VueAxios, axios);
Vue.component('vue-navigation-bar', VueNavigationBar);
Vue.component('VerticalSidebar', () => import('@/layouts/vertical/Index'));
Vue.component('VuePerfectScrollbar', VuePerfectScrollbar);

Vue.config.productionTip = false;

// Initialize Firebase
firebase.initializeApp({
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
});

firebase
  .auth()
  .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
  .then(() => {})
  .catch((error) => {
    console.error('Error setting Firebase Auth persistence: ', error);
  });

new Vue({
  store,
  router,
  vuetify,
  beforeCreate() {
    // Vue.config.productionTip = false

    firebase.auth().onAuthStateChanged(async (user) => {
      if (this.$store.state.isEmailVerificationTab) {
        await this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.CREDENTIALS);
        await this.$store.dispatch('changeSignupDialogState', false);

        await this.$store.dispatch('showAlertDialog', {
          title: 'You are logged in to another tab.',
          subTitle: 'Please close this tab and continue your session in another one.',
          permanent: true,
          doNotClose: true,
        });

        return;
      }

      if (!user) {
        this.handleLogout();
        return;
      }

      if (window.location.pathname !== '/game_error') {
        this.loadZohoChat();
      }

      // TODO: here we prevent login during registration via social networks, but maybe we need to activate it after phone verification
      if (!user.emailVerified) return;

      // TODO: check this lock to work properly
      if (this.isSocialSignUp) {
        return;
      }

      if (!this.$store.state.isManualSignIn) {
        await this.$store.dispatch('autoSignIn');
      }

      const session = localStorage.getItem('session');
      if (!session) {
        await this.$store.dispatch('setLoggedIn', true);
      }
    });

    let mode = localStorage.getItem('currency') || CURRENCIES.MMC;

    // handle 2nd login
    if (localStorage.getItem('setCurrencyToMc')) {
      localStorage.removeItem('setCurrencyToMc');
      mode = CURRENCIES.MMC;
    }

    const rec = { mode };

    this.$store.dispatch('setPlayMode', rec);
    this.$store.dispatch('notifyMe', rec);
  },

  computed: {
    isLoggedIn() {
      return this.$store.state.isLoggedIn;
    },

    isSocialSignUp() {
      return this.$store.state.isSocialSignUp;
    },

    displayName() {
      return this.$store.getters.getuser?.displayName;
    },

    playerInfo() {
      return this.$store.getters.getPlayerInfo;
    },

    authState() {
      return { user: this.$store.state.user, token: this.$store.state.firebaseToken };
    },
  },

  watch: {
    authState: {
      handler(newVal, oldVal) {
        if (newVal.user === oldVal.user && newVal.token === oldVal.token) {
          return;
        }

        // init the socket only when the auth state changes, and we already have a token
        if (newVal.user && !oldVal.user && newVal.token) {
          this.initWebSocket();
        } else if (newVal.user && newVal.token && newVal.token !== oldVal.token) {
          this.initWebSocket();
        } else if (!newVal.user) {
          this.destroyWebSocket();
        }
      },
      deep: true,
    },

    isLoggedIn(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.processLoggedInUser();
      }
    },
  },

  created() {
    EventBus.on('user-suspended', this.onUserSuspended);
  },

  mounted() {
    this.detectFocusOut();

    this.$store.dispatch('getGoogleRecaptchaParam').then((script) => {
      const scriptElement = document.createElement('script');
      scriptElement.src = script.script;

      document.head.appendChild(scriptElement);
    });
  },

  beforeDestroy() {
    EventBus.off('user-suspended', this.onUserSuspended);
    this.destroyWebSocket();
  },

  methods: {
    async processLoggedInUser() {
      const user = this.authState?.user || this.playerInfo?.profile;

      if (!user) {
        return;
      }

      await Promise.all([
        this.$store.dispatch('getPlayerInfo'),
        this.$store.dispatch('getActiveGames'),
        this.$store.dispatch('getfriendslist'),
        this.$store.dispatch('getLastPlayedWith'),
        this.$store.dispatch('initRealTimeAlerts'),
        this.$store.dispatch('initRealTimeChallenges'),
        this.$store.dispatch('initRealTimeRoomsPlayerNumber'),
        this.$store.dispatch('getReferralLink'),
        this.$store.dispatch('getReferrals'),
        // this.$store.dispatch('loadUserBoughtPackages'),
        // this.$store.dispatch('getPackages'),
      ]);

      const displayName = this.displayName || this.playerInfo?.profile?.displayName;

      if (displayName) {
        this.setDatabaseListeners();
      } else {
        console.error(`The "Displayname" field does not exist for user: ${user.uid}`);
        console.error(`User data: ${JSON.stringify(user)}`);
      }

      this.setDatadogUser(user);

      this.$store.dispatch('tryToShowPromoPopup');
    },

    async handleLogout() {
      this.$store.commit('setUser', '');

      if (window.location.pathname !== '/verifyemail' && !this.isSocialSignUp) {
        this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.CREDENTIALS);
      }

      if (typeof datadogRum !== 'undefined') {
        datadogRum.clearUser();
      }

      this.removeZohoChat();
    },

    setDatabaseListeners() {
      const db = firebase.database();

      const connectedRef = db.ref('.info/connected');
      connectedRef?.on('value', async (snapshot) => {
        if (snapshot.val() === false) return;

        websocket.emit('playing', { isPlaying: !!this.$store.getters.getIFrameDialog });
      });
    },

    setDatadogUser(user) {
      if (!this.playerInfo) return;

      datadogRum.setUser({
        id: user.uid,
        name: this.playerInfo.profile?.displayName,
        email: user.email,
        publicIp: this.playerInfo.profile?.ipAddress,
      });
    },

    detectFocusOut() {
      let disconnectTimeout = null;
      document.onvisibilitychange = () => {
        clearTimeout(disconnectTimeout);

        if (document.visibilityState === 'hidden') {
          disconnectTimeout = setTimeout(() => firebase.database().goOffline(), 300000);
        } else {
          firebase.database().goOnline();
        }
      };
    },

    loadZohoChat() {
      window.$zoho = window.$zoho || {};
      window.$zoho.salesiq = window.$zoho.salesiq || {
        widgetcode: 'siq639342da0d696bcbd7e301691602218394a13865e086197e9bbbe9068b207007',
        values: {},
        ready: function () {},
      };
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'zsiqscript';
      script.defer = true;
      script.src = 'https://salesiq.zoho.eu/widget';

      script.onload = () => {
        if (typeof window.$zoho !== 'undefined' && $zoho.salesiq) {
          $zoho.salesiq.floatwindow?.close();
          $zoho.salesiq.floatbutton?.visible('hide');
          window.zohoChatIsHidden = true;
        }
      };

      const tag = document.getElementsByTagName('script')[0];
      tag.parentNode.insertBefore(script, tag);
    },

    removeZohoChat() {
      if (typeof window.$zoho !== 'undefined' && window.$zoho.salesiq) {
        window.$zoho.salesiq.floatbutton?.visible('hide');
        window.$zoho.salesiq.chatbutton?.visible('hide');
      }
    },

    initWebSocket() {
      this.$store.dispatch('initWebSocket');
    },

    destroyWebSocket() {
      this.$store.dispatch('destroyWebSocket');
    },

    onUserSuspended() {
      if (this.$route.fullPath === '/profileuser/yourprofile') return;
      this.$router.push('/profileuser/yourprofile');
    },
  },

  render: (h) => h(App),
}).$mount('#app');
